/**
 * Joins what PostHog reports against what the app actually contains.
 *
 * The registry is the authoritative list of features — PostHog only knows
 * about features someone has *opened*. That asymmetry is the whole point: a
 * feature that appears in the registry and never in the analytics is the most
 * interesting row on the page, and it can only be found by diffing the two.
 */

import { features } from "@/features/registry";

import type { FeatureUsage } from "./api";

export interface FeatureRow extends FeatureUsage {
  /** True when the registry knows this feature but PostHog has never seen it. */
  neverOpened: boolean;
  /** False when PostHog reports a key the registry no longer has (a rename). */
  inRegistry: boolean;
}

/**
 * One row per registry feature, plus any orphan keys PostHog still reports.
 *
 * Orphans are kept rather than dropped: after a feature is renamed the old key
 * lingers in historical events, and silently hiding it would make a chunk of
 * past usage vanish with no explanation.
 */
export function buildFeatureRows(usage: FeatureUsage[]): FeatureRow[] {
  const byKey = new Map(usage.map((u) => [u.feature_key, u]));
  const rows: FeatureRow[] = [];

  for (const f of features) {
    const hit = byKey.get(f.key);
    byKey.delete(f.key);
    rows.push({
      feature_key: f.key,
      label: f.nav.label,
      nav_group: f.nav.group ?? null,
      events: hit?.events ?? 0,
      users: hit?.users ?? 0,
      prior_events: hit?.prior_events ?? 0,
      prior_users: hit?.prior_users ?? 0,
      exceptions: hit?.exceptions ?? 0,
      delta_events: hit?.delta_events ?? 0,
      neverOpened: (hit?.events ?? 0) === 0 && (hit?.prior_events ?? 0) === 0,
      inRegistry: true,
    });
  }

  for (const orphan of byKey.values()) {
    rows.push({ ...orphan, neverOpened: false, inRegistry: false });
  }

  rows.sort((a, b) => b.events - a.events || a.feature_key.localeCompare(b.feature_key));
  return rows;
}

/** Heat tone for a matrix cell, scaled against the busiest cell in the grid. */
export function heatTone(
  value: number,
  max: number,
): "neutral" | "brand" | "success" | "warning" {
  if (value <= 0) return "neutral";
  if (max <= 0) return "neutral";
  const share = value / max;
  if (share >= 0.66) return "brand";
  if (share >= 0.33) return "success";
  return "warning";
}

export function formatWindow(start: string, end: string): string {
  if (!start || !end) return "";
  const fmt = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return iso;
    // Construct in local time — `new Date("2026-08-11")` parses as UTC and
    // renders as the 10th in US timezones.
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}
